import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiFetch } from '../api/client';

interface Doctor {
  doctor_id: number;
  name: string;
  email: string;
  specialty: string;
  department: string;
  has_face_enrolled: boolean;
}

interface Patient {
  patient_id: number;
  mrn: string;
  name: string;
  age: number;
  gender: string;
  date_of_birth: string | null;
  contact_number: string;
}

interface UserContextType {
  doctor: Doctor | null;
  patient: Patient | null;
  doctors: Doctor[];
  patients: Patient[];
  setDoctor: (doctor: Doctor | null) => void;
  setPatient: (patient: Patient | null) => void;
  loadDoctors: () => Promise<void>;
  loadPatients: (search?: string) => Promise<void>;
  reloadCurrentDoctor: () => Promise<void>;
  isLoading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

  const loadDoctors = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/doctors`, {
        headers: {
          'ngrok-skip-browser-warning': 'true', // Skip ngrok interstitial page
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDoctors(data.doctors || []);

        // Auto-select first doctor if none selected
        if (!doctor && data.doctors && data.doctors.length > 0) {
          setDoctor(data.doctors[0]);
          localStorage.setItem('selected_doctor_id', String(data.doctors[0].doctor_id));
        }
      }
    } catch (error) {
      console.error('Failed to load doctors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPatients = async (search?: string) => {
    setIsLoading(true);
    try {
      const url = search
        ? `${API_BASE_URL}/api/users/patients?search=${encodeURIComponent(search)}`
        : `${API_BASE_URL}/api/users/patients`;
        // const response = await fetch(url, {
      const response = await apiFetch(url, {
        headers: {
          'ngrok-skip-browser-warning': 'true', // Skip ngrok interstitial page
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPatients(data.patients || []);
        // Do not auto-select a patient; user must select from dashboard line list
      }
    } catch (error) {
      console.error('Failed to load patients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const reloadCurrentDoctor = async () => {
    if (!doctor) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/doctors/${doctor.doctor_id}`, {
        headers: {
          'ngrok-skip-browser-warning': 'true', // Skip ngrok interstitial page
        },
      });
      if (response.ok) {
        const data = await response.json();
        const updatedDoctor = data.doctor;
        setDoctor(updatedDoctor);

        // Also update in doctors list
        setDoctors((prev) =>
          prev.map((d) => (d.doctor_id === updatedDoctor.doctor_id ? updatedDoctor : d))
        );
      }
    } catch (error) {
      console.error('Failed to reload current doctor:', error);
    }
  };

  // Load from localStorage on mount
  useEffect(() => {
    const savedDoctorId = localStorage.getItem('selected_doctor_id');
    const savedPatientId = localStorage.getItem('selected_patient_id');

    // Load doctors and patients
    loadDoctors();
    loadPatients();

    // Restore selections if IDs exist
    if (savedDoctorId) {
      // Will be set when doctors load
    }
    if (savedPatientId) {
      // Will be set when patients load
    }
  }, []);

  // Save to localStorage when changed
  useEffect(() => {
    if (doctor) {
      localStorage.setItem('selected_doctor_id', String(doctor.doctor_id));
      localStorage.setItem('selected_doctor', JSON.stringify(doctor));
    }
  }, [doctor]);

  useEffect(() => {
    if (patient) {
      localStorage.setItem('selected_patient_id', String(patient.patient_id));
      localStorage.setItem('selected_patient', JSON.stringify(patient));
    }
  }, [patient]);

  return (
    <UserContext.Provider
      value={{
        doctor,
        patient,
        doctors,
        patients,
        setDoctor,
        setPatient,
        loadDoctors,
        loadPatients,
        reloadCurrentDoctor,
        isLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
